/**
 * useParentAsk — the pupil's £5 goes through a parent (job #102).
 *
 * A class-joined pupil never sees a card form. Every pupil £5 door
 * (useCheckout.startCheckout with a schoolClassId) lands here instead: the
 * pupil enters a parent or carer's email, the server mails that address a pay
 * link, and the purchase lands on the pupil's own account when the parent pays
 * (views/ParentPay.vue → the unchanged student_via_teacher webhook).
 *
 * Module-level state, like useCheckout's overlay, so the one card mounted in
 * App.vue answers every entry point.
 */
import { ref } from 'vue'
import { resolveSupabase } from './schools/client'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const open = ref(false)
const classId = ref<string | null>(null)
const sending = ref(false)
const sentTo = ref<string | null>(null)
const errorKey = ref<'' | 'badEmail' | 'failed'>('')
const errorText = ref('')
// A closed or replaced card must not receive a previous submission's result.
let cardVersion = 0

export function useParentAsk() {
  function openAsk(forClassId: string) {
    cardVersion++
    sending.value = false
    classId.value = forClassId
    sentTo.value = null
    errorKey.value = ''
    errorText.value = ''
    open.value = true
  }
  function close() {
    cardVersion++
    sending.value = false
    open.value = false
  }
  function again() {
    sentTo.value = null
    errorKey.value = ''
    errorText.value = ''
  }
  async function send(rawEmail: string): Promise<boolean> {
    const email = rawEmail.trim()
    if (!EMAIL_RE.test(email)) {
      errorKey.value = 'badEmail'
      errorText.value = ''
      return false
    }
    if (!classId.value || sending.value) return false
    // Keep this submission's class across the asynchronous session lookup.
    const submittedClassId = classId.value
    const submittedVersion = cardVersion
    sending.value = true
    errorKey.value = ''
    errorText.value = ''
    try {
      const client = resolveSupabase(null)
      const { data: { session } } = (await client?.auth.getSession()) ?? { data: { session: null } }
      const res = await fetch('/api/pupil/parent-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token ?? ''}` },
        body: JSON.stringify({ classId: submittedClassId, parentEmail: email }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        if (submittedVersion !== cardVersion) return false
        errorKey.value = 'failed'
        errorText.value = typeof body?.error === 'string' ? body.error : ''
        return false
      }
      if (submittedVersion === cardVersion) sentTo.value = email
      return true
    } catch {
      if (submittedVersion === cardVersion) errorKey.value = 'failed'
      return false
    } finally {
      if (submittedVersion === cardVersion) sending.value = false
    }
  }
  return { open, sending, sentTo, errorKey, errorText, openAsk, close, again, send }
}
